import { z } from 'zod'
import type { ThankYouResult } from 'fundraising-contentful'
import {
  interpolateDonorName,
  zSurveyConfig,
  type FormDocumentV1,
  type FormThanksV1,
  type SurveyConfig,
} from 'fundraising-core'
import { THANK_YOU_SURVEY_ID, thankYouSurvey } from 'fundraising-core/thank-you-quiz'
import { cloudinaryUrl } from './donation'
import { htmlToText, sanitizeFormHtml } from './html'

/** Where a heading greets the donor by first name. */
export const NAME_SLOT = '{fname}'

/** The second-ask types this reader shows. */
export const KNOWN_ASK_TYPES: readonly string[] = ['link', 'quiz']

/** A link to another form or page, with amounts that ride along (act's SecondAsk). */
export const zLinkAskV1 = z.object({
  type: z.literal('link'),
  kicker: z.string(),
  heading: z.string().min(1),
  body: z.string(),
  image: z.string(),
  imageAlt: z.string().nullable().optional(),
  url: z.string().regex(/^https:\/\/\S+$/),
  presets: z.array(z.number().int().positive()).min(1).max(4),
  /** The amount preselected; absent: the first preset. */
  defaultSum: z.number().int().positive().optional(),
  /** The ask's own currency; absent: the page's. */
  currency: z.enum(['SEK', 'EUR']).optional(),
  interval: z.enum(['once', 'month']),
})
export type LinkAskV1 = z.infer<typeof zLinkAskV1>

/**
 * A quiz for the thank-you page. At most one per `next`; not shown on the aborted page. `surveyId`
 * is the quiz key (a copy is `<key>--<slug>`); a reader parses `config` as a SurveyConfig (`quizConfigOf`).
 */
export const zQuizAskV1 = z.object({
  type: z.literal('quiz'),
  surveyId: z.string().min(1),
  config: z.unknown(),
})
export type QuizAskV1 = z.infer<typeof zQuizAskV1>

const parseQuiz = (ask: unknown): QuizAskV1 | null => {
  const parsed = zQuizAskV1.safeParse(ask)
  return parsed.success ? parsed.data : null
}

const configOf = (ask: QuizAskV1): SurveyConfig | null => {
  const parsed = zSurveyConfig.safeParse(ask.config)
  return parsed.success ? parsed.data : null
}

/** The usable config of the quiz ask with this surveyId, or null: no such ask, or its config doesn't parse. */
export const quizConfigOf = (
  next: readonly unknown[] | undefined,
  surveyId: string,
): SurveyConfig | null => {
  const ask = (next ?? []).map(parseQuiz).find((quiz) => quiz?.surveyId === surveyId)
  return ask ? configOf(ask) : null
}

const typeOf = (ask: unknown): string =>
  typeof ask === 'object' && ask !== null && 'type' in ask ? String(ask.type) : '?'

/** The asks this reader can show, in order. */
export const knownAsks = (next: readonly unknown[] | undefined): LinkAskV1[] =>
  (next ?? []).flatMap((ask) => {
    const parsed = zLinkAskV1.safeParse(ask)
    return parsed.success ? [parsed.data] : []
  })

/** The first quiz ask, and whether it opens the list (no link ask before it). */
export const knownQuiz = (
  next: readonly unknown[] | undefined,
): { ask: QuizAskV1; first: boolean } | null => {
  let shown = 0
  for (const ask of next ?? []) {
    const quiz = parseQuiz(ask)
    if (quiz) return { ask: quiz, first: shown === 0 }
    if (zLinkAskV1.safeParse(ask).success) shown++
  }
  return null
}

/** What the knowns leave out: an unknown type by name, a known one that doesn't parse as invalid. */
export const droppedAsks = (next: readonly unknown[] | undefined): string[] => {
  let quizzes = 0
  return (next ?? []).flatMap((ask) => {
    const type = typeOf(ask)
    const ok =
      type === 'quiz'
        ? parseQuiz(ask) !== null && ++quizzes === 1
        : zLinkAskV1.safeParse(ask).success
    return ok ? [] : [KNOWN_ASK_TYPES.includes(type) ? `${type} (invalid)` : type]
  })
}

export interface FormResultAsk {
  kicker: string
  heading: string
  bodyHtml: string
  /** A delivery URL, or '' for none. */
  image: string
  /** As authored; '' when none, and the host draws the image as decorative (the heading sits beside it). */
  imageAlt: string
  url: string
  sums: number[]
  /** The amount preselected; absent: the first of `sums`. */
  defaultSum?: number
  /** The ask's own currency; absent: the page's. */
  currency?: 'SEK' | 'EUR'
  monthly: boolean
}

/** The quiz a page offers. */
export interface FormResultQuiz {
  surveyId: string
  /** The document's config; null when it doesn't parse as a SurveyConfig, which the host decides on. */
  config: SurveyConfig | null
  /** Opens the list: the quiz is the page's first ask. */
  first: boolean
}

/** A thank-you page, sanitized and ready for a host to draw. */
export interface FormResultPage {
  kicker: string
  /** May hold NAME_SLOT. */
  heading: string
  leadHtml: string
  leadText: string
  bodyHtml: string
  /** A delivery URL for the image behind the ask, or '' for none. */
  heroImage: string
  /** As authored; '' when none. */
  heroImageAlt: string
  ask: FormResultAsk | null
  /** Absent reads as none. */
  quiz?: FormResultQuiz | null
}

/** What a result state shows: the form's own page, a Contentful entry, or nothing known. */
export type ResultChoice = { page: FormResultPage } | { sysId: string } | null
export interface ResultChoices {
  ok: ResultChoice
  postal: ResultChoice
  aborted: ResultChoice
}

const toAsk = (ask: LinkAskV1): FormResultAsk => ({
  kicker: ask.kicker,
  heading: ask.heading,
  bodyHtml: sanitizeFormHtml(ask.body),
  image: cloudinaryUrl(ask.image),
  imageAlt: ask.imageAlt ?? '',
  url: ask.url,
  sums: [...ask.presets],
  ...(ask.defaultSum ? { defaultSum: ask.defaultSum } : {}),
  ...(ask.currency ? { currency: ask.currency } : {}),
  monthly: ask.interval === 'month',
})

export function toResultPage(thanks: FormThanksV1, next?: readonly unknown[]): FormResultPage {
  const [ask] = knownAsks(next)
  const quiz = knownQuiz(next)
  const leadHtml = sanitizeFormHtml(thanks.lead)
  return {
    kicker: thanks.kicker,
    heading: thanks.heading,
    leadHtml,
    leadText: htmlToText(leadHtml),
    bodyHtml: sanitizeFormHtml(thanks.body),
    heroImage: cloudinaryUrl(thanks.heroImage ?? ''),
    heroImageAlt: thanks.heroImageAlt ?? '',
    ask: ask ? toAsk(ask) : null,
    quiz: quiz
      ? { surveyId: quiz.ask.surveyId, config: configOf(quiz.ask), first: quiz.first }
      : null,
  }
}

/**
 * Which page each result state shows. The form's own page wins, then the Contentful entry its
 * results name; a pending gift falls back to the paid gift's choice. An aborted page asks for
 * nothing: the payment just failed. Mirrors dr_forms ReaderRules.
 */
export function resultChoices(
  form: Pick<FormDocumentV1, 'thanks' | 'pending' | 'aborted' | 'results' | 'next'>,
): ResultChoices {
  const { done, pending, aborted } = form.results
  const ok: ResultChoice = form.thanks
    ? { page: toResultPage(form.thanks, form.next) }
    : done
      ? { sysId: done }
      : null
  const postal: ResultChoice = form.pending
    ? { page: toResultPage(form.pending, form.next) }
    : pending
      ? { sysId: pending }
      : ok
  const abortedChoice: ResultChoice = form.aborted
    ? { page: toResultPage(form.aborted) }
    : aborted
      ? { sysId: aborted }
      : null
  return { ok, postal, aborted: abortedChoice }
}

/**
 * A page as fundraising-contentful's `ThankYouResult`, the way `mapInteractionEndpoint` reads an
 * entry: the lead stands alone as `body`, or leads the rich text in `bodyHtml`.
 */
export function toThankYouResult(page: FormResultPage): ThankYouResult {
  const ask = page.ask
  return {
    title: page.heading.split(NAME_SLOT).join('{name}').trim() || undefined,
    subtitle: page.kicker.trim() || undefined,
    body: page.bodyHtml ? undefined : page.leadText || undefined,
    bodyHtml: page.bodyHtml ? page.leadHtml + page.bodyHtml : undefined,
    confetti: true,
    heroImage: page.heroImage || undefined,
    heroImageAlt: page.heroImageAlt || undefined,
    secondAsks: ask
      ? [
          {
            title: ask.heading || undefined,
            subtitle: ask.kicker || undefined,
            description: htmlToText(ask.bodyHtml) || undefined,
            image: ask.image || undefined,
            imageAlt: ask.imageAlt || undefined,
            url: ask.url,
            sums: ask.sums,
            defaultSum: ask.defaultSum,
            currency: ask.currency && (ask.currency === 'EUR' ? '€' : 'kr'),
          },
        ]
      : undefined,
  }
}

/**
 * A choice whose quiz a host can render. A config that doesn't parse falls back to the registered
 * (Swedish) quiz when it is that one on a Swedish page; any other quiz is dropped and `broken` is
 * told, since Drupal never exports a quiz it can't use.
 */
export function resolveQuizChoice(
  choice: ResultChoice,
  lang: string,
  broken: (surveyId: string) => void,
): ResultChoice {
  if (!choice || !('page' in choice) || !choice.page.quiz || choice.page.quiz.config) return choice
  const { quiz } = choice.page
  if (quiz.surveyId === THANK_YOU_SURVEY_ID && lang === 'sv')
    return { page: { ...choice.page, quiz: { ...quiz, config: thankYouSurvey } } }
  broken(quiz.surveyId)
  return { page: { ...choice.page, quiz: null } }
}

/** Each state's quiz made usable (resolveQuizChoice), each broken quiz reported once. */
export function resolveQuizChoices(
  choices: ResultChoices,
  lang: string,
  onError: (error: Error, surveyId: string) => void,
): ResultChoices {
  const reported = new Set<string>()
  const keep = (choice: ResultChoice) =>
    resolveQuizChoice(choice, lang, (id) => {
      if (reported.has(id)) return
      reported.add(id)
      onError(new Error('quiz ask has no usable config'), id)
    })
  return { ok: keep(choices.ok), postal: keep(choices.postal), aborted: keep(choices.aborted) }
}

/**
 * A once-per-key gate for reports: true the first time a key is seen in this process. Keyed by
 * document version, it reports a bad form once per version rather than on every read. Forgets
 * everything past `max` keys.
 */
export function createFirstReport(max = 500): (key: string) => boolean {
  const seen = new Set<string>()
  return (key) => {
    if (seen.has(key)) return false
    if (seen.size >= max) seen.clear()
    seen.add(key)
    return true
  }
}

/** What the invite card shows: the config's hero, and its header title when it has no invite title. */
export function quizInviteOf(
  quiz: { first: boolean; config: SurveyConfig | null } | null,
  firstName?: string,
): { title: string; body?: string; heroImage?: string; heroImageAlt?: string } | null {
  const config = quiz?.config
  if (!quiz || quiz.first || !config) return null
  const { header } = config
  return {
    title: header.inviteTitle || interpolateDonorName(header.title, firstName),
    body: header.inviteBody || undefined,
    heroImage: config.heroImage,
    heroImageAlt: config.heroImageAlt,
  }
}
